/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IMoeV2TopMenuBarViewProps } from './moe-v2-top-menu-bar';

interface ProcessedMenuItem {
    id: string;
    label: string;
    linkUrl: string;
    isActive: boolean;
    activeColor: string;
    inactiveColor: string;
    itemAriaLabel: string;
}

export default (props: IMoeV2TopMenuBarViewProps) => {
    const {
        config: { menuItems = [], activeColor, inactiveColor, menuBackgroundColor },
        currentPath = '',
        mobileOpen = false,
        setMobileOpen
    } = props;

    // Helper function to get the URL from link data
    const getLinkUrl = (link: any): string => {
        if (!link) {
            return '';
        }

        if (typeof link === 'string') {
            return link;
        }
        if (link.destinationUrl) {
            return link.destinationUrl;
        }
        if (link.linkUrl) {
            return link.linkUrl;
        }
        if (link.href) {
            return link.href;
        }

        return '';
    };

    // Helper function to check if a link is active
    const isLinkActive = (linkUrl: string, currentPath: string): boolean => {
        if (!linkUrl || !currentPath) {
            return false;
        }

        const normalizeUrl = (url: string): string => {
            try {
                const urlObj = new URL(url, window.location.origin);
                return urlObj.pathname.toLowerCase().replace(/\/+$/, '') || '/';
            } catch {
                return url.toLowerCase().replace(/\/+$/, '') || '/';
            }
        };

        const normalizedLinkUrl = normalizeUrl(linkUrl);
        const normalizedCurrentPath = normalizeUrl(currentPath);
        return normalizedLinkUrl === normalizedCurrentPath;
    };

    // Process ALL menu items in memory FIRST
    const processedItems: ProcessedMenuItem[] = React.useMemo(() => {
        return menuItems
            .map((item, idx) => {
                const linkUrl = getLinkUrl(item.link);

                if (!item.label || !linkUrl) {
                    return null;
                }

                const isActive = isLinkActive(linkUrl, currentPath);
                const itemAriaLabel = `Navigate to ${item.itemAriaLabel}`;

                const processedItem: ProcessedMenuItem = {
                    id: `menu-item-${idx}`,
                    label: item.label,
                    linkUrl,
                    isActive,
                    activeColor: activeColor ? activeColor : '#000',
                    inactiveColor: inactiveColor ? inactiveColor : '#b3c6e7',
                    itemAriaLabel
                };

                return processedItem;
            })
            .filter((item): item is ProcessedMenuItem => item !== null);
    }, [menuItems, currentPath, activeColor, inactiveColor]);

    const handleHamburgerClick = () => {
        setMobileOpen && setMobileOpen(!mobileOpen);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleHamburgerClick();
        }
    };

    return (
        <nav
            className='moe-v2-module-container moe-top-menu-bar'
            style={
                {
                    // Set CSS custom property for background color
                    '--moe-menu-bg-color': menuBackgroundColor || '#0a4ea1'
                } as React.CSSProperties
            }
            aria-label={props.config.moduleAriaLabel || 'Top Menu Bar'}
        >
            <button
                className='moe-top-menu-bar__hamburger'
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={!!mobileOpen}
                aria-controls='moe-top-menu-bar-list'
                onClick={handleHamburgerClick}
                onKeyDown={handleKeyDown}
                type='button'
                style={{
                    backgroundColor: '#000'
                }}
            >
                <span style={{ backgroundColor: '#fff' }} />
                <span style={{ backgroundColor: '#fff' }} />
                <span style={{ backgroundColor: '#fff' }} />
            </button>

            <ul
                className={`moe-top-menu-bar__list${mobileOpen ? ' moe-top-menu-bar__list--open' : ''}`}
                id='moe-top-menu-bar-list'
                role='menubar'
            >
                {processedItems.map(item => {
                    const itemClassName = `moe-top-menu-bar__item${item.isActive ? ' active' : ''}`;

                    return (
                        <li key={item.id} className={itemClassName} role='none'>
                            <a
                                href={item.linkUrl}
                                role='menuitem'
                                aria-label={item.label}
                                tabIndex={0}
                                title={item.itemAriaLabel}
                                ref={linkEl => {
                                    if (linkEl) {
                                        // Ensure colors are applied correctly
                                        const targetColor = item.isActive ? item.activeColor : item.inactiveColor;
                                        const targetWeight = item.isActive ? 'bold' : 'normal';

                                        linkEl.style.color = targetColor;
                                        linkEl.style.fontWeight = targetWeight;
                                    }
                                }}
                                style={{
                                    color: item.isActive ? item.activeColor : item.inactiveColor,
                                    fontWeight: item.isActive ? 'bold' : 'normal',
                                    textDecoration: 'none',
                                    fontSize: '15px',
                                    letterSpacing: '0.05em',
                                    transition: 'color 0.2s',
                                    padding: '0 4px',
                                    display: 'block',
                                    height: '48px',
                                    lineHeight: '48px',
                                    background: 'none',
                                    border: 'none',
                                    outline: 'none',
                                    width: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                                aria-current={item.isActive ? 'page' : undefined}
                                onFocus={e => {
                                    e.currentTarget.style.outline = '2px solid #fff';
                                    e.currentTarget.style.outlineOffset = '2px';
                                }}
                                onBlur={e => {
                                    e.currentTarget.style.outline = 'none';
                                }}
                            >
                                {item.label}
                            </a>
                            {item.isActive && (
                                <span
                                    className='moe-top-menu-bar__arrow'
                                    aria-hidden='true'
                                    style={{
                                        display: 'block',
                                        position: 'absolute',
                                        top: '100%',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '0',
                                        height: '0',
                                        borderLeft: '8px solid transparent',
                                        borderRight: '8px solid transparent',
                                        borderBottom: '8px solid #fff',
                                        borderTop: 'none',
                                        marginTop: '-8px',
                                        zIndex: 10,
                                        pointerEvents: 'none'
                                    }}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};
